import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Avatar,
  Button,
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
  TextField,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CopyToClipboardButton from '../../component/CopyClipBoardButton';
import SocialMediaLinks from './socialMediaLinks';
import ChatButtonIcon from '../../svg/ChatButtonIcon';
import Textboxstar from '../../svg/textboxstar';
import JournalIcon from '../../svg/JournalIcon';
import { useAuth } from '../../hooks/useAuth';
import { Timestamp } from 'firebase/firestore';
import { useTheme } from '@mui/material/styles';
import SendChatIcon from '../../svg/SendChatIcon';
import { useNavigate } from 'react-router-dom';
import { drawerWidth, drawerWidthClosed } from '../leftNav';
import { getPrices } from '../../utils/api';
import AttachMoneyIcon from '../../svg/Profilenavicons/PricecardIcon';
import { useProfileData } from '../../hooks/useProfileData';
import UserProfileSections from './UserProfileSections';
import TabSection from './TabSection';
// import MvAiReplies from './MvAiReplies';
import MvPosts from './MvPosts';
import MvReplies from './MvReplies';
import { AblyWebsocketChannelProvider } from '../../component/AblyWebsockets/AblyWebsocketChannelProvider';
import PremiumPurchaseButtonProfilePage from '../Purchases/premiumPurchaseButtonProfilePage';
import { useChannel } from 'ably/react';
import { PostType, CommentType } from '../memeverse/memeverse_types';
import CreateUpdateComment from '../memeverse/CreateUpdateComment';
import RefreshIcon from '@mui/icons-material/Refresh';
import { logging } from '../../utils/logger';

const UserProfilePage = () => {
  return (
    <AblyWebsocketChannelProvider channelName='ask'>
      <UserProfileData />
    </AblyWebsocketChannelProvider>
  );
};

const UserProfileData = () => {
  const { authUser } = useAuth();
  const { publish } = useChannel('ask');
  const { creatorProfileData: profileData } = useProfileData();
  const authDocumentId = authUser?.documentId;
  // const isProfileAuthUser = authUser?.documentId === profileData?.userId;

  const [premiumAccess, setPremiumAccess] = useState<boolean>(false);

  const message = useRef(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const [soopraSub, setSoopraSub] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [replyingTo, setReplyingTo] = useState<PostType | CommentType | null>(
    null
  );
  const [showRefreshButton, setShowRefreshButton] = useState<boolean>(false);

  const {
    userId = '',
    fullName = '',
    profilePicUrl = '',
    userName = '',
    occupation = '',
    index = '',
    // shortBio = '',
    subscription,
    // introMessage,
  } = profileData || {};

  async function fetchSubscriptions() {
    try {
      getPrices([profileData?.subscription?.custom_product_id])
        .then((res) => {
          if (res.ok) {
            return res.json();
          }
        })
        .then((data) => {
          let priceInfo = (data || {})[
            profileData?.subscription?.custom_product_id
          ];
          if (priceInfo) {
            setSoopraSub(priceInfo);
          }
        });
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  }

  useEffect(() => {
    fetchSubscriptions();
  }, [profileData]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (subscription?.custom_product_id) {
      let access = false;
      let premiumKey = subscription?.custom_product_id;
      if (userId === authDocumentId) {
        access = true;
      } else if (authUser?.premiumPurchases) {
        let currentTimeStamp = Timestamp.fromDate(new Date());
        if (authUser.premiumPurchases[premiumKey]) {
          // check if not expired
          if (authUser.premiumPurchases[premiumKey] > currentTimeStamp) {
            access = true;
          }
        }
      }
      if (premiumAccess !== access) setPremiumAccess(access);
    }
  }, [authUser, subscription]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (replyingTo) {
      publish('comment.thinking.new', {
        userId: authUser?.uid,
        parentId: replyingTo.docId,
        level: 'comments_counts' in replyingTo ? 0 : 1,
        userType: 'user',
        user_details: {
          userName: authUser?.userName,
          profilePicUrl: authUser?.profilePicUrl,
          fullName: authUser?.fullName,
          user_id: authUser?.uid,
        },
      });
    }
    return () => {
      if (replyingTo) {
        publish('comment.thinking.remove', {
          userId: authUser?.uid,
          parentId: replyingTo.docId,
          level: 'level' in replyingTo ? replyingTo.level + 1 : 0,
          userType: 'user',
        });
      }
    };
  }, [replyingTo, authUser, publish]);

  useEffect(() => {
    if (!index) {
      const timer = setTimeout(() => {
        setShowRefreshButton(true);
      }, 30000); // Show refresh button after 30 seconds
      return () => clearTimeout(timer);
    }
  }, [index])

  const handleSendMessage = (inputStr?: string) => {
    if (subscription?.custom_product_id && authUser && !premiumAccess) {
      if (inputStr || message.current?.value?.trim()) {
        // just navigate the user if they dont have premium access, dont send the message
        navigate(`/${userName}/chat`);
      }
    } else if (inputStr) {
      navigate(`/${userName}/chat`, { state: { message: inputStr } });
    } else if (message.current?.value?.trim()) {
      navigate(`/${userName}/chat`, {
        state: { message: message.current?.value },
      });
    }
  };

  const handleHardRefresh = () => {
    logging.info('User has requested a hard refresh of the page on userProfileData');
    window.location.reload();
  };

  return (
    <Box
      display='flex'
      flexDirection='column'
      flex='1'
      width='100%'
      height='100%'
    >
      <Box
        sx={{
          py: { xs: 3, md: 4 },
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Outer container for Avatar and Name/Socials */}
        <Box
          display='flex'
          flexDirection={{ xs: 'column', md: 'row' }} // Column layout for mobile, row for larger screens
          alignItems={{ xs: 'center', md: 'flex-start' }}
          gap={{ xs: 1.5, md: 4 }} // Add spacing between avatar and text
        >
          {/* Avatar */}
          <Box position='relative'>
            <Avatar
              src={profilePicUrl}
              alt={fullName}
              sx={{
                width: { xs: '124px', md: '211px' },
                height: { xs: '120px', md: '203px' },
                border: '2px solid #fff',
                borderRadius: '35%',
                objectFit: 'cover',
              }}
            >
              {fullName ? fullName[0] : ''}
            </Avatar>
          </Box>

          {/* Name/Socials and Buttons */}
          <Box
            display='flex'
            flexDirection='column'
            justifyContent='space-between'
            sx={{
              width: '100%',
              textAlign: { xs: 'center', md: 'left' }, // Center text on mobile, left-align on larger screens
              height: { xs: '100%', md: '203px' },
            }}
          >
            {/* TOP SECTION (Name, Socials) */}
            <Box
              sx={{
                mb: { xs: 1.5, MozTextDecorationStyle: 0 },
              }}
            >
              {/* Full Name + Verified */}
              <Box
                display='flex'
                alignItems='center'
                justifyContent={{ xs: 'center', md: 'flex-start' }}
              >
                <Typography variant='h6' fontWeight={600}>
                  {fullName}
                </Typography>
              </Box>

              {/* Username */}
              <Typography
                variant='body2'
                fontWeight={400}
                color='text.secondary'
              >
                @{userName}
              </Typography>

              {/* Occupation */}
              <Typography
                variant='body2'
                fontWeight={400}
                color='text.secondary'
              >
                {occupation}
              </Typography>

              {/* Subscription Details */}
              {soopraSub && (
                <Box
                  display='flex'
                  flexDirection='column'
                  sx={{
                    borderRadius: '4px',
                    my: 0.5,
                  }}
                >
                  <Typography
                    variant='body1'
                    sx={{
                      fontFamily: 'fontFamily',
                      fontWeight: 'fontWeightRegular',
                      fontSize: '0.875rem',
                      lineHeight: '20.02px',
                      letterSpacing: '0.17px',
                      color: 'text.secondary',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: { xs: 'center', md: 'flex-start' },
                    }}
                  >
                    <AttachMoneyIcon color={theme.palette.primary.main} />
                    <Box component='span' sx={{ pl: 1 }}>
                      {' '}
                      {/* Padding added here */}
                      {`Subscription - $${
                        Math.round(soopraSub.unit_amount || 0) / 100
                      }/${soopraSub.recurring?.interval || 'month'}`}
                    </Box>
                  </Typography>
                </Box>
              )}

              {/* Social Media Links  */}
              <SocialMediaLinks profileData={profileData} />
            </Box>

            {/* BOTTOM SECTION (Buttons) Mobile */}
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' }, // Show this layout only on mobile
                flexDirection: 'column',
                gap: 1.5,
                justifyContent: 'center',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  width: '100%',
                  gap: 1.5,
                }}
              >
                {/* Chat Button */}
                <Button
                  id='button-chat'
                  component='a'
                  href={`/${profileData.userName}/chat`}
                  variant='contained'
                  size='large'
                  color='primary'
                  disabled={!index}
                  startIcon={<ChatButtonIcon />}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate(`/${profileData.userName}/chat`);
                  }}
                  sx={{
                    width:
                      !!soopraSub && authDocumentId !== userId
                        ? 'auto'
                        : '100%',
                  }}
                >
                  Chat
                </Button>

                {!!soopraSub && authDocumentId !== userId && (
                  <PremiumPurchaseButtonProfilePage
                    requiredPremiumKey={
                      profileData?.subscription?.custom_product_id
                    }
                    redirectURL={`/${profileData.userName}/chat`}
                  />
                )}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-around',
                  gap: 1.5,
                }}
              >
                {/* Journal Button */}
                {authDocumentId === userId && (
                  <Link to='update/journaling' style={{ flex: 1 }}>
                    <Button
                      id='button-journaling'
                      variant='outlined'
                      size='large'
                      fullWidth
                      disabled={!index}
                      startIcon={<JournalIcon />}
                      sx={{
                        flexGrow: 1,
                      }}
                    >
                      Journal
                    </Button>
                  </Link>
                )}

                {/* Edit Profile Button */}
                {authDocumentId === userId && (
                  <Link to={`/${userName}/update`} style={{ flex: 1 }}>
                    <Button
                      id={`button-edit-info-${userName}`}
                      variant='outlined'
                      size='large'
                      fullWidth
                      sx={{
                        whiteSpace: 'nowrap',
                        flexGrow: 1,
                      }}
                      startIcon={<EditOutlinedIcon />}
                    >
                      Edit Profile
                    </Button>
                  </Link>
                )}
              </Box>
            </Box>

            {/* Desktop and larger screens layout */}
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' }, // Show this layout only on larger screens
                flexDirection: 'row',
                gap: 1,
              }}
            >
              {/* Chat Button */}
              <Link to={`/${userName}/chat`} style={{ flex: 0 }}>
                <Button
                  id='button-chat'
                  variant='contained'
                  size='large'
                  color='primary'
                  disabled={!index}
                  startIcon={<ChatButtonIcon />}
                >
                  Chat
                </Button>
              </Link>

              {/* Journal Button */}
              {authDocumentId === userId && (
                <Link to='update/journaling' style={{ flex: 0 }}>
                  <Button
                    id='button-journaling'
                    variant='outlined'
                    size='large'
                    disabled={!index}
                    startIcon={<JournalIcon />}
                  >
                    Journal
                  </Button>
                </Link>
              )}

              {/* Edit Profile Button */}
              {authDocumentId === userId && (
                <Link to={`/${userName}/update`} style={{ flex: 0 }}>
                  <Button
                    id={`button-edit-info-${userName}`}
                    variant='outlined'
                    size='large'
                    startIcon={<EditOutlinedIcon />}
                    sx={{
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Edit Profile
                  </Button>
                </Link>
              )}

              {!!soopraSub && authDocumentId !== userId && (
                <PremiumPurchaseButtonProfilePage
                  requiredPremiumKey={
                    profileData?.subscription?.custom_product_id
                  }
                  redirectURL={`/${profileData.userName}/chat`}
                />
              )}

              {/* Share Button */}
              <Box>
                <CopyToClipboardButton
                  copyLink={
                    !!profileData?.userName
                      ? `${window.location.origin}/${profileData?.userName}/chat`
                      : ''
                  }
                  buttonProps={{
                    disabled: !index,
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        <TabSection activeTab={activeTab} setActiveTab={setActiveTab} />

        {activeTab === 'profile' && <UserProfileSections />}

        <>
          {activeTab === 'posts' && (
            <MvPosts
              userId={userId}
              onReplyClick={(item) => setReplyingTo(item)}
            />
          )}

          {activeTab === 'replies' && (
            <MvReplies
              userId={userId}
              onReplyClick={(item) => setReplyingTo(item)}
            />
          )}

          {/* will make this work in future */}
          {/* {activeTab === 'aiReplies' && <MvAiReplies />} */}
        </>

        <Box width='100%' position='relative'>
          {/* Floating Textbox */}
          <Box
            sx={{
              position: 'fixed',
              bottom: {
                xs: '60px',
                md: '16px',
              },
              left: {
                xs: '50%',
                md: `calc((${drawerWidthClosed + drawerWidth}px)/2 + 50%)`,
              },
              transform: 'translateX(-50%)',
              width: {
                xs: '90%',
                md: `calc(100% - ${drawerWidthClosed + drawerWidth}px)`,
              },
              maxWidth: '800px',
              height: '44px',
              backgroundColor: theme.palette.background.paper,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '100px',
              padding: {
                xs: '8px 0px 8px 8px',
                md: '8px 0px 8px 8px',
              },
              boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
              display: 'flex',
              alignItems: 'center',
              zIndex: 10,
            }}
          >
            <Box
              sx={{
                marginLeft: '8px', // Space from the left edge
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Textboxstar />
            </Box>
            <TextField
              variant='outlined'
              placeholder='Ask anything...'
              fullWidth
              autoComplete='off'
              inputRef={message}
              defaultValue=''
              slotProps={{
                input: {
                  sx: {
                    borderRadius: '100px',
                    paddingRight: '12px',
                    '& fieldset': { border: 'none' },
                  },
                },
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleSendMessage();
                }
              }}
            />
            <IconButton
              color='primary'
              onClick={() => handleSendMessage()}
              sx={{
                ml: '12px', // Adjust spacing between the input and the button
              }}
            >
              <SendChatIcon isTextEntered={true} />
            </IconButton>
          </Box>
        </Box>
        <Box width='100%' mt={3}>
          {!index && (
            <Box
              display='flex'
              justifyContent='center'
              alignItems='center'
              textAlign='center'
              flexDirection='column'
              gap={2}
              my={3}
            >
              <Typography
                variant='body1'
                fontWeight={500}
                sx={{
                  justifyContent: 'center',
                  padding: 0.8,
                  borderRadius: 'none',
                  width: '100%',
                  maxWidth: '280px',
                }}
              >
                Your personality is being created! It may take a few minutes.
              </Typography>
              <CircularProgress
                size={30}
                sx={{
                  color: 'text.secondary',
                  display: showRefreshButton ? 'none' : 'block',
                }}
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={handleHardRefresh}
                sx={{
                  display: showRefreshButton ? 'block' : 'none',
                }}
              >
                Waiting a while? Try Refreshing
              </Button>
            </Box>
          )}

          {/* Premium subscription button */}
          {/* {
              (subscription?.custom_product_id && !premiumAccess) ?
              <Suspense >
                <Box p={2.5}>
                  <PremiumPurchaseButton requiredPremiumKey={subscription.custom_product_id} redirectURL={`/${userName}/chat`}/>
                </Box>
              </Suspense> : <></>
            } */}
          <Box></Box>
        </Box>
      </Box>
      {replyingTo && (
        <Dialog
          open={!!replyingTo}
          onClose={() => setReplyingTo(null)}
          fullWidth
          maxWidth='md'
          slotProps={{
            backdrop: {
              sx: {
                display: { xs: 'none', md: 'block' },
              },
            },
          }}
          PaperProps={{
            sx: {
              m: 0,
              [theme.breakpoints.down('md')]: {
                width: '100%',
                maxWidth: '100%',
                height: 'calc(100% - 107px)',
                maxHeight: 'none',
                boxShadow: 'none',
                mt: '50px',
              },
            },
          }}
          sx={{
            [theme.breakpoints.down('md')]: {
              '& .MuiDialog-container': {
                alignItems: 'flex-start',
              },
            },
          }}
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <CreateUpdateComment
            setCreateUpdateCommentVisible={() => setReplyingTo(null)}
            parentPostOrComment={replyingTo}
          />
        </Dialog>
      )}
    </Box>
  );
};

export default UserProfilePage;
